import React, { useEffect, useRef, useState } from 'react';
import ArrayPlayground from './ArrayPlayground';
import HashPlayground from './HashPlayground';
import TwoPointerPlayground from './TwoPointerPlayground';
import SlidingWindowPlayground from './SlidingWindowPlayground';
import StackPlayground from './StackPlayground';
import BinarySearchPlayground from './BinarySearchPlayground';
import LinkedListPlayground from './LinkedListPlayground';
import TreePlayground from './TreePlayground';
import TriePlayground from './TriePlayground';
import HeapPlayground from './HeapPlayground';
import BacktrackingPlayground from './BacktrackingPlayground';
import GraphPlayground from './GraphPlayground';
import AdvancedGraphPlayground from './AdvancedGraphPlayground';
import Dp1DPlayground from './Dp1DPlayground';
import Dp2DPlayground from './Dp2DPlayground';
import GreedyPlayground from './GreedyPlayground';
import IntervalsPlayground from './IntervalsPlayground';
import MathBitPlayground from './MathBitPlayground';
import MatrixPlayground from './MatrixPlayground';
import PalindromesPlayground from './PalindromesPlayground';
import DsaDrillModal from './DsaDrillModal';

const V1 = '#7F00FF', V2 = '#B163FF', GREEN = '#2ECC71', RED = '#F04155', ORANGE = '#E8834C';

// Pattern-specific interactive sandboxes. Patterns without an entry here
// fall back to the plain step visualizer + quiz built from content.visualizer/content.quiz.
const PLAYGROUNDS = {
  Arrays: ArrayPlayground,
  Hashing: HashPlayground,
  'Two Pointers': TwoPointerPlayground,
  'Sliding Window': SlidingWindowPlayground,
  Stack: StackPlayground,
  'Binary Search': BinarySearchPlayground,
  'Linked List': LinkedListPlayground,
  Trees: TreePlayground,
  Tries: TriePlayground,
  'Heap / Priority Queue': HeapPlayground,
  Backtracking: BacktrackingPlayground,
  Graphs: GraphPlayground,
  'Advanced Graphs': AdvancedGraphPlayground,
  '1-D Dynamic Programming': Dp1DPlayground,
  '2-D Dynamic Programming': Dp2DPlayground,
  Greedy: GreedyPlayground,
  Intervals: IntervalsPlayground,
  'Math & Bit Manipulation': MathBitPlayground,
  Matrix: MatrixPlayground,
  Palindromes: PalindromesPlayground,
};

/* ─── shared bits ─────────────────────────────────────────── */
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CodeBlock = ({ code, note }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1400); });
  };
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)' }}>
        <button onClick={copy} style={{
          fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
          border: `1px solid ${copied ? GREEN : 'var(--border)'}`,
          background: copied ? 'rgba(46,204,113,0.12)' : 'transparent',
          color: copied ? GREEN : 'var(--muted)', cursor: 'pointer',
        }}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <pre style={{ margin: 0, padding: '18px 20px', overflowX: 'auto', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 'clamp(14px, 0.8vw + 12px, 15.5px)', lineHeight: 1.75, color: '#E8E1FF' }}>
        <code>{code}</code>
      </pre>
      {note && <p style={{ margin: 0, padding: '12px 18px', fontSize: 'clamp(13px, 0.4vw + 12px, 14.5px)', lineHeight: 1.65, color: 'var(--muted)', borderTop: '1px solid var(--border)' }}>{note}</p>}
    </div>
  );
};

/* ─── Memory diagram (used by 'memory-diagram' sections) ────── */
const MemoryDiagram = ({ demo }) => {
  const { values, baseAddress, bytesPerElement } = demo;
  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      <div style={{ display: 'flex', gap: 4, width: 'max-content' }}>
        {values.map((v, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4, fontFamily: 'monospace' }}>{baseAddress + i * bytesPerElement}</div>
            <div style={{
              width: 54, height: 54, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `linear-gradient(135deg, ${V1}18, ${V2}0c)`, border: `1.5px solid ${V1}40`,
              fontWeight: 800, fontSize: 15, color: 'var(--text)', fontFamily: "'Inter',sans-serif",
            }}>{v}</div>
            <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 4, fontWeight: 700 }}>arr[{i}]</div>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, fontFamily: 'monospace' }}>
        address(i) = {baseAddress} + i × {bytesPerElement}
      </p>
    </div>
  );
};

/* ─── Bucket diagram (used by 'bucket-diagram' sections) ────── */
const BucketDiagram = ({ demo }) => {
  const { capacity, entries, note } = demo;
  const buckets = Array.from({ length: capacity }, () => []);
  entries.forEach(e => buckets[e.hash % capacity].push(e));

  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      <div style={{ display: 'flex', gap: 10, width: 'max-content' }}>
        {buckets.map((chain, i) => (
          <div key={i} style={{ textAlign: 'center', minWidth: 84 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              bucket {i}
            </div>
            <div style={{
              minHeight: 50, borderRadius: 10, padding: '8px 7px',
              border: `1.5px solid ${chain.length ? `${V1}40` : 'var(--border)'}`,
              background: chain.length ? `${V1}0c` : 'var(--bg3)',
              display: 'flex', flexDirection: 'column', gap: 6,
              alignItems: 'center', justifyContent: chain.length ? 'flex-start' : 'center',
            }}>
              {chain.length === 0 && <span style={{ color: 'var(--muted)', fontSize: 13 }}>·</span>}
              {chain.map((e, ci) => (
                <span key={ci} style={{
                  fontSize: 12, fontWeight: 700, padding: '4px 9px', borderRadius: 7, whiteSpace: 'nowrap',
                  background: `linear-gradient(135deg,${V1},${V2})`, color: '#fff',
                }}>{e.key}</span>
              ))}
            </div>
            {chain.length > 1 && (
              <div style={{ fontSize: 9.5, color: '#F04155', fontWeight: 700, marginTop: 4 }}>collision — chained</div>
            )}
          </div>
        ))}
      </div>
      {note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, fontFamily: 'monospace' }}>{note}</p>}
    </div>
  );
};

/* ─── Pointer diagram (used by 'pointer-diagram' sections) ──── */
const PointerDiagram = ({ demo }) => {
  const { values, pointers, note } = demo;
  const at = i => pointers.filter(p => p.index === i);
  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      <div style={{ display: 'flex', gap: 8, width: 'max-content', paddingTop: 30 }}>
        {values.map((v, i) => {
          const here = at(i);
          const active = here.length > 0;
          return (
            <div key={i} style={{ position: 'relative', textAlign: 'center' }}>
              {here.map((p, pi) => (
                <div key={pi} style={{
                  position: 'absolute', top: -30, left: '50%', transform: 'translateX(-50%)',
                  fontSize: 10.5, fontWeight: 800, color: p.color || V1, whiteSpace: 'nowrap',
                }}>{p.label} ↓</div>
              ))}
              <div style={{
                width: 50, height: 50, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: active ? `linear-gradient(135deg,${here[0].color || V1},${V2})` : 'var(--bg3)',
                border: `1.5px solid ${active ? (here[0].color || V1) : 'var(--border)'}`,
                color: active ? '#fff' : 'var(--text)', fontWeight: 800, fontSize: 16,
                fontFamily: "'Inter',sans-serif", transition: 'all 0.2s ease',
              }}>{v}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>{i}</div>
            </div>
          );
        })}
      </div>
      {note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 14, fontFamily: 'monospace' }}>{note}</p>}
    </div>
  );
};

/* ─── Window diagram (used by 'window-diagram' sections) ────── */
const WindowDiagram = ({ demo }) => {
  const { values, left, right, label } = demo;
  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      <div style={{ display: 'flex', gap: 8, width: 'max-content', paddingTop: 28 }}>
        {values.map((v, i) => {
          const inWindow = i >= left && i <= right;
          return (
            <div key={i} style={{ position: 'relative', textAlign: 'center' }}>
              {i === left && (
                <div style={{ position: 'absolute', top: -26, left: '50%', transform: 'translateX(-50%)', fontSize: 10, fontWeight: 800, color: V1, whiteSpace: 'nowrap' }}>left</div>
              )}
              {i === right && (
                <div style={{ position: 'absolute', top: -26, left: '50%', transform: 'translateX(-50%)', fontSize: 10, fontWeight: 800, color: V2, whiteSpace: 'nowrap' }}>right</div>
              )}
              <div style={{
                width: 50, height: 50, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: inWindow ? `linear-gradient(135deg,${V1},${V2})` : 'var(--bg3)',
                border: `1.5px solid ${inWindow ? V1 : 'var(--border)'}`,
                color: inWindow ? '#fff' : 'var(--text)', fontWeight: 800, fontSize: 16,
                fontFamily: "'Inter',sans-serif",
              }}>{v}</div>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>{i}</div>
              {inWindow && (
                <div style={{
                  position: 'absolute', left: 2, right: 2, bottom: -18, height: 2,
                  background: `linear-gradient(90deg,${V1},${V2})`, borderRadius: 2,
                }} />
              )}
            </div>
          );
        })}
      </div>
      {label && <p style={{ fontSize: 13, color: V2, fontWeight: 700, marginTop: 18 }}>{label}</p>}
    </div>
  );
};

/* ─── Stack diagram (used by 'stack-diagram' sections) ───────── */
const StackDiagram = ({ demo }) => {
  const { values, note } = demo;
  const topIndex = values.length - 1;
  return (
    <div style={{ margin: '18px 0 4px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: V2, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>top</div>
      <div style={{ display: 'flex', flexDirection: 'column-reverse', gap: 4 }}>
        {values.map((v, i) => (
          <div key={i} style={{
            width: 140, height: 42, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative', fontWeight: 800, fontSize: 15, fontFamily: "'Inter',sans-serif",
            background: i === topIndex ? `linear-gradient(135deg,${V1},${V2})` : 'var(--bg3)',
            border: `1.5px solid ${i === topIndex ? V1 : 'var(--border)'}`,
            color: i === topIndex ? '#fff' : 'var(--text)',
          }}>
            {v}
            {i === topIndex && (
              <span style={{ position: 'absolute', right: -46, fontSize: 10.5, fontWeight: 800, color: V2, whiteSpace: 'nowrap' }}>← top</span>
            )}
          </div>
        ))}
      </div>
      <div style={{
        width: 156, height: 3, marginTop: 4, borderRadius: 2,
        background: 'var(--border-strong)',
      }} />
      {note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 14, fontFamily: 'monospace', textAlign: 'center' }}>{note}</p>}
    </div>
  );
};

/* ─── Node diagram (used by 'node-diagram' sections) ─────────── */
const NodeDiagram = ({ demo }) => {
  const { nodes, pointers = [], cycleTo, note } = demo;
  const at = i => pointers.filter(p => p.index === i);
  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, width: 'max-content', paddingTop: 30 }}>
        {nodes.map((n, i) => {
          const here = at(i);
          const active = here.length > 0;
          return (
            <React.Fragment key={i}>
              <div style={{ position: 'relative', textAlign: 'center' }}>
                {here.map((p, pi) => (
                  <div key={pi} style={{
                    position: 'absolute', top: -28 - pi * 16, left: '50%', transform: 'translateX(-50%)',
                    fontSize: 10.5, fontWeight: 800, color: p.color || V1, whiteSpace: 'nowrap',
                  }}>{p.label} ↓</div>
                ))}
                <div style={{
                  width: 54, height: 54, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: active ? `linear-gradient(135deg,${here[0].color || V1},${V2})` : 'var(--bg3)',
                  border: `1.5px solid ${active ? (here[0].color || V1) : 'var(--border)'}`,
                  color: active ? '#fff' : 'var(--text)', fontWeight: 800, fontSize: 15,
                  fontFamily: "'Inter',sans-serif", transition: 'all 0.2s ease',
                }}>{n}</div>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>{i}</div>
              </div>
              {i < nodes.length - 1 && (
                <span style={{ padding: '0 6px', color: 'var(--muted)', fontSize: 18, flexShrink: 0 }}>→</span>
              )}
            </React.Fragment>
          );
        })}
        {cycleTo === undefined ? (
          <>
            <span style={{ padding: '0 6px', color: 'var(--muted)', fontSize: 18, flexShrink: 0 }}>→</span>
            <div style={{
              padding: '0 12px', height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 10, border: '1.5px dashed var(--border)', color: 'var(--muted)',
              fontSize: 12.5, fontWeight: 700, fontFamily: 'monospace',
            }}>null</div>
          </>
        ) : (
          <>
            <span style={{ padding: '0 6px', color: RED, fontSize: 18, flexShrink: 0 }}>↩</span>
            <div style={{
              padding: '0 14px', height: 54, display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 10, border: `1.5px dashed ${RED}`, color: RED,
              fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
            }}>back to node {cycleTo}</div>
          </>
        )}
      </div>
      {note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 16, fontFamily: 'monospace' }}>{note}</p>}
    </div>
  );
};

/* ─── Tree diagram (used by 'tree-diagram' sections) ──────────── */
const TreeNodeBox = ({ node, highlight }) => {
  if (!node) return <span style={{ color: 'var(--muted)', fontSize: 12, fontFamily: 'monospace' }}>null</span>;
  const active = highlight && highlight.includes(node.val);
  return (
    <div>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 40, height: 40, padding: '0 10px', borderRadius: 10,
        background: active ? `linear-gradient(135deg,${V1},${V2})` : 'var(--bg3)',
        border: `1.5px solid ${active ? V1 : 'var(--border)'}`,
        color: active ? '#fff' : 'var(--text)', fontWeight: 800, fontSize: 15,
        fontFamily: "'Inter',sans-serif",
      }}>{node.val}</span>
      {(node.left || node.right) && (
        <div style={{
          marginLeft: 18, paddingLeft: 16, marginTop: 10,
          borderLeft: '2px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {node.left && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: V2, marginTop: 11, flexShrink: 0 }}>L</span>
              <TreeNodeBox node={node.left} highlight={highlight} />
            </div>
          )}
          {node.right && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: '#3B82F6', marginTop: 11, flexShrink: 0 }}>R</span>
              <TreeNodeBox node={node.right} highlight={highlight} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
const TreeDiagram = ({ demo }) => {
  const { tree, highlight, note } = demo;
  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      <TreeNodeBox node={tree} highlight={highlight} />
      {note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 16, fontFamily: 'monospace' }}>{note}</p>}
    </div>
  );
};

/* ─── Trie diagram (used by 'trie-diagram' sections) ──────────── */
const TrieNodeBox = ({ node, label, path, highlight }) => {
  const active = highlight && highlight.includes(path);
  const children = node.children ? Object.entries(node.children) : [];
  return (
    <div>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
        minWidth: 38, height: 38, padding: '0 9px', borderRadius: 10,
        background: active ? `linear-gradient(135deg,${V1},${V2})` : 'var(--bg3)',
        border: `1.5px solid ${active ? V1 : 'var(--border)'}`,
        color: active ? '#fff' : 'var(--text)', fontWeight: 800, fontSize: 14,
        fontFamily: "'Fira Code','Consolas',monospace",
      }}>
        {label}
        {node.isEnd && (
          <span style={{
            position: 'absolute', bottom: -5, right: -5, width: 11, height: 11, borderRadius: '50%',
            background: GREEN, border: '2px solid var(--card-solid)',
          }} />
        )}
      </span>
      {children.length > 0 && (
        <div style={{
          marginLeft: 18, paddingLeft: 16, marginTop: 10,
          borderLeft: '2px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {children.map(([ch, child]) => (
            <TrieNodeBox key={ch} node={child} label={ch} path={path + ch} highlight={highlight} />
          ))}
        </div>
      )}
    </div>
  );
};
const TrieDiagram = ({ demo }) => {
  const { trie, highlight, note } = demo;
  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      <TrieNodeBox node={trie} label="•" path="" highlight={highlight} />
      <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: GREEN }} /> = end of a complete word
      </p>
      {note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4, fontFamily: 'monospace' }}>{note}</p>}
    </div>
  );
};

/* ─── Graph diagram (used by 'graph-diagram' sections) ────────── */
const GraphDiagram = ({ demo }) => {
  const { nodes, edges, directed, highlightNodes = [], highlightEdges = [] } = demo;
  const n = nodes.length;
  const R = 82, cx = 110, cy = 110;
  const pos = nodes.map((_, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return { x: cx + R * Math.cos(angle), y: cy + R * Math.sin(angle) };
  });
  const nodeIndex = val => nodes.indexOf(val);
  const isEdgeHighlighted = (a, b) => highlightEdges.some(([x, y]) => (x === a && y === b) || (!directed && x === b && y === a));
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <svg width="220" height="220" viewBox="0 0 220 220">
        {directed && (
          <defs>
            <marker id="dsaGraphArrow" markerWidth="8" markerHeight="8" refX="15" refY="4" orient="auto">
              <polygon points="0 0, 8 4, 0 8" style={{ fill: 'var(--muted)' }} />
            </marker>
          </defs>
        )}
        {edges.map(([a, b, weight], i) => {
          const ai = nodeIndex(a), bi = nodeIndex(b);
          if (ai === -1 || bi === -1) return null;
          const hl = isEdgeHighlighted(a, b);
          const mx = (pos[ai].x + pos[bi].x) / 2, my = (pos[ai].y + pos[bi].y) / 2;
          return (
            <g key={i}>
              <line
                x1={pos[ai].x} y1={pos[ai].y} x2={pos[bi].x} y2={pos[bi].y}
                style={{ stroke: hl ? V1 : 'var(--border-strong)' }}
                strokeWidth={hl ? 3 : 1.5}
                markerEnd={directed ? 'url(#dsaGraphArrow)' : undefined}
              />
              {weight !== undefined && (
                <>
                  <circle cx={mx} cy={my} r="10" style={{ fill: 'var(--card-solid)', stroke: hl ? V1 : 'var(--border)' }} strokeWidth="1" />
                  <text x={mx} y={my + 3.5} textAnchor="middle" fontSize="10" fontWeight="700" style={{ fill: hl ? V1 : 'var(--muted)', fontFamily: 'monospace' }}>{weight}</text>
                </>
              )}
            </g>
          );
        })}
        {nodes.map((val, i) => {
          const hl = highlightNodes.includes(val);
          return (
            <g key={val}>
              <circle cx={pos[i].x} cy={pos[i].y} r="16" style={{ fill: hl ? V1 : 'var(--bg3)', stroke: hl ? V1 : 'var(--border)' }} strokeWidth="1.5" />
              <text x={pos[i].x} y={pos[i].y + 5} textAnchor="middle" fontSize="13" fontWeight="800" style={{ fill: hl ? '#fff' : 'var(--text)', fontFamily: "'Inter',sans-serif" }}>{val}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/* ─── DP table diagram (used by 'dp-table-diagram' sections) ──── */
const DpTableDiagram = ({ demo }) => {
  const { values, labelPrefix = 'dp', highlightIndices = [], note } = demo;
  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      <div style={{ display: 'flex', gap: 4, width: 'max-content' }}>
        {values.map((v, i) => {
          const hl = highlightIndices.includes(i);
          return (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4, fontFamily: 'monospace' }}>{labelPrefix}[{i}]</div>
              <div style={{
                width: 54, height: 54, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: hl ? `linear-gradient(135deg, ${V1}, ${V2})` : `linear-gradient(135deg, ${V1}18, ${V2}0c)`,
                border: `1.5px solid ${hl ? V1 : `${V1}40`}`,
                fontWeight: 800, fontSize: 15, color: hl ? '#fff' : 'var(--text)', fontFamily: "'Inter',sans-serif",
              }}>{v}</div>
              <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 4, fontWeight: 700 }}>i={i}</div>
            </div>
          );
        })}
      </div>
      {note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, fontFamily: 'monospace' }}>{note}</p>}
    </div>
  );
};

/* ─── Grid diagram (used by 'grid-diagram' sections) ──────────── */
const GridDiagram = ({ demo }) => {
  const { grid, highlightCells = [], note } = demo;
  const isHighlighted = (r, c) => highlightCells.some(([hr, hc]) => hr === r && hc === c);
  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 'max-content' }}>
        {grid.map((row, r) => (
          <div key={r} style={{ display: 'flex', gap: 4 }}>
            {row.map((cell, c) => {
              const hl = isHighlighted(r, c);
              return (
                <div key={c} style={{
                  width: 48, height: 48, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: hl ? `linear-gradient(135deg, ${V1}, ${V2})` : `linear-gradient(135deg, ${V1}18, ${V2}0c)`,
                  border: `1.5px solid ${hl ? V1 : `${V1}40`}`,
                  fontWeight: 800, fontSize: 14, color: hl ? '#fff' : 'var(--text)', fontFamily: "'Inter',sans-serif",
                }}>{cell === null || cell === undefined ? '·' : cell}</div>
              );
            })}
          </div>
        ))}
      </div>
      {note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, fontFamily: 'monospace' }}>{note}</p>}
    </div>
  );
};

/* ─── Interval diagram (used by 'interval-diagram' sections) ──── */
const IntervalDiagram = ({ demo }) => {
  const { intervals, min, max, highlightIndices = [], note } = demo;
  const range = (max - min) || 1;
  const pct = v => ((v - min) / range) * 100;
  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 320 }}>
        {intervals.map(([s, e], i) => {
          const hl = highlightIndices.includes(i);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 54, fontSize: 11, fontFamily: 'monospace', color: 'var(--muted)', flexShrink: 0, textAlign: 'right' }}>[{s},{e}]</span>
              <div style={{ position: 'relative', flex: 1, height: 22, background: 'var(--bg3)', borderRadius: 6 }}>
                <div style={{
                  position: 'absolute', top: 0, bottom: 0,
                  left: `${pct(s)}%`, width: `${Math.max(pct(e) - pct(s), 1.5)}%`,
                  borderRadius: 6,
                  background: hl ? `linear-gradient(90deg,${V1},${V2})` : `linear-gradient(90deg,${V1}55,${V2}35)`,
                  border: `1.5px solid ${hl ? V1 : `${V1}50`}`,
                }} />
              </div>
            </div>
          );
        })}
      </div>
      {note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, fontFamily: 'monospace' }}>{note}</p>}
    </div>
  );
};

/* ─── Bit diagram (used by 'bit-diagram' sections) ─────────────── */
const BitDiagram = ({ demo }) => {
  const { bits, highlightIndices = [], label, note } = demo;
  return (
    <div style={{ margin: '18px 0 4px', overflowX: 'auto' }}>
      {label && <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>}
      <div style={{ display: 'flex', gap: 4, width: 'max-content' }}>
        {bits.map((b, i) => {
          const hl = highlightIndices.includes(i);
          return (
            <div key={i} style={{
              width: 34, height: 40, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: hl ? `linear-gradient(135deg,${V1},${V2})` : (b === '1' ? `${V1}18` : 'var(--bg3)'),
              border: `1.5px solid ${hl ? V1 : (b === '1' ? `${V1}40` : 'var(--border)')}`,
              fontWeight: 800, fontSize: 15, color: hl ? '#fff' : 'var(--text)', fontFamily: "'Fira Code','Consolas',monospace",
            }}>{b}</div>
          );
        })}
      </div>
      {note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, fontFamily: 'monospace' }}>{note}</p>}
    </div>
  );
};

/* ─── section type renderers ─────────────────────────────────── */
const TextSection = ({ section }) => (
  <div>
    {section.heading && <div style={{ fontSize: 'clamp(13.5px, 0.5vw + 12px, 15px)', fontWeight: 800, color: V2, marginBottom: 12 }}>{section.heading}</div>}
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
  </div>
);

const MemorySection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <MemoryDiagram demo={section.demo} />}
  </div>
);

const BucketSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <BucketDiagram demo={section.demo} />}
  </div>
);

const PointerSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <PointerDiagram demo={section.demo} />}
  </div>
);

const WindowSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <WindowDiagram demo={section.demo} />}
  </div>
);

const StackSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <StackDiagram demo={section.demo} />}
  </div>
);

const NodeSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <NodeDiagram demo={section.demo} />}
  </div>
);

const TreeSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <TreeDiagram demo={section.demo} />}
  </div>
);

const TrieSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <TrieDiagram demo={section.demo} />}
  </div>
);

const GraphSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <GraphDiagram demo={section.demo} />}
    {section.demo?.note && <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 14, fontFamily: 'monospace', textAlign: 'center' }}>{section.demo.note}</p>}
  </div>
);

const DpTableSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <DpTableDiagram demo={section.demo} />}
  </div>
);

const GridDiagramSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <GridDiagram demo={section.demo} />}
  </div>
);

const IntervalDiagramSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <IntervalDiagram demo={section.demo} />}
  </div>
);

const BitDiagramSection = ({ section }) => (
  <div>
    {section.body.map((p, i) => (
      <p key={i} style={{ fontSize: 'clamp(15px, 0.9vw + 12px, 17px)', lineHeight: 1.85, color: 'var(--text-2)', marginBottom: 14 }}>{p}</p>
    ))}
    {section.demo && <BitDiagram demo={section.demo} />}
  </div>
);

const CodeSection = ({ section }) => <CodeBlock code={section.code} note={section.note} />;

const ErrorsSection = ({ section }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    {section.items.map((it, i) => (
      <div key={i} style={{ border: `1px solid ${RED}30`, borderRadius: 12, padding: '16px 18px', background: `${RED}08` }}>
        <div style={{ fontSize: 'clamp(15px, 0.5vw + 13px, 16.5px)', fontWeight: 700, color: RED, marginBottom: 8 }}>{it.mistake}</div>
        <div style={{ fontSize: 'clamp(14px, 0.5vw + 12px, 15.5px)', lineHeight: 1.65, color: 'var(--text-2)', marginBottom: 6 }}><b style={{ color: 'var(--text)' }}>Why: </b>{it.why}</div>
        <div style={{ fontSize: 'clamp(14px, 0.5vw + 12px, 15.5px)', lineHeight: 1.65, color: 'var(--text-2)' }}><b style={{ color: GREEN }}>Fix: </b>{it.fix}</div>
      </div>
    ))}
  </div>
);

const ComplexitySection = ({ section }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(14px, 0.5vw + 12px, 15.5px)' }}>
      <thead>
        <tr>
          <th style={thStyle}>Operation</th>
          <th style={thStyle}>Time</th>
          <th style={thStyle}>Why</th>
        </tr>
      </thead>
      <tbody>
        {section.rows.map((r, i) => (
          <tr key={i}>
            <td style={tdStyle}>{r.op}</td>
            <td style={{ ...tdStyle, fontWeight: 800, color: V2, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.time}</td>
            <td style={{ ...tdStyle, color: 'var(--muted)' }}>{r.note}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
const thStyle = { textAlign: 'left', padding: '12px 14px', borderBottom: '1.5px solid var(--border)', color: 'var(--muted)', fontSize: 'clamp(12px, 0.3vw + 11px, 13px)', textTransform: 'uppercase', letterSpacing: '0.04em' };
const tdStyle = { padding: '12px 14px', borderBottom: '1px solid var(--border)', color: 'var(--text)' };

const LEVEL_COLOR = { Easy: GREEN, Medium: ORANGE, Advanced: RED };
const ProblemsSection = ({ section }) => {
  const [openDrill, setOpenDrill] = useState(null);
  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        {section.groups.map(g => (
          <div key={g.level}>
            <div style={{
              display: 'inline-block', fontSize: 11.5, fontWeight: 800, color: LEVEL_COLOR[g.level] || V2,
              background: `${LEVEL_COLOR[g.level] || V2}15`, padding: '3px 10px', borderRadius: 999, marginBottom: 10,
            }}>{g.level}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {g.items.map((it, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                  borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg3)',
                }}>
                  <span style={{ flex: 1, fontSize: 'clamp(14px, 0.5vw + 12px, 15.5px)', lineHeight: 1.6, color: 'var(--text)' }}>{it.title}</span>
                  <button
                    onClick={() => setOpenDrill(it)}
                    style={{
                      flexShrink: 0, fontSize: 'clamp(12px, 0.3vw + 11px, 13px)', fontWeight: 800, padding: '7px 15px', borderRadius: 999,
                      border: `1.5px solid ${V1}`, background: 'transparent', color: V1, cursor: 'pointer',
                      fontFamily: "'Satoshi','DM Sans',sans-serif",
                    }}
                  >Code</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {section.note && <p style={{ fontSize: 'clamp(13px, 0.4vw + 12px, 14.5px)', color: 'var(--muted)', marginTop: 18, lineHeight: 1.6 }}>{section.note}</p>}

      {openDrill && <DsaDrillModal drill={openDrill} onClose={() => setOpenDrill(null)} />}
    </div>
  );
};

const CheatsheetSection = ({ section }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    {section.items.map((it, i) => (
      <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <span style={{ color: V1, marginTop: 2 }}><CheckIcon /></span>
        <span style={{ fontSize: 'clamp(14px, 0.5vw + 12px, 15.5px)', lineHeight: 1.65, color: 'var(--text)' }}>{it}</span>
      </div>
    ))}
  </div>
);

/* ─── Fallback playground: old step visualizer + quiz, for patterns
       without a bespoke sandbox registered in PLAYGROUNDS ─────── */
const FallbackVisualizer = ({ visualizer }) => {
  const { problem, steps } = visualizer;
  const [step, setStep] = useState(0);
  const cur = steps[step];
  const nums = problem.match(/\[(.*?)\]/)[1].split(',').map(s => parseInt(s.trim(), 10));
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--muted)', marginBottom: 18 }}>{problem}</div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 22, flexWrap: 'wrap' }}>
        {nums.map((n, i) => {
          const isCurrent = i === cur.i;
          const isMatch = cur.found && (i === cur.i || i === cur.matchIndex);
          return (
            <div key={i} style={{
              width: 52, height: 52, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 17, fontWeight: 800, fontFamily: "'Inter',sans-serif",
              background: isMatch ? `linear-gradient(135deg,${GREEN},#27AE60)` : isCurrent ? `linear-gradient(135deg,${V1},${V2})` : 'var(--bg3)',
              color: isMatch || isCurrent ? '#fff' : 'var(--text)',
              border: `1.5px solid ${isMatch ? GREEN : isCurrent ? V1 : 'var(--border)'}`,
            }}>{n}</div>
          );
        })}
      </div>
      <div style={{ padding: '14px 18px', borderRadius: 12, fontSize: 14, lineHeight: 1.6, background: cur.found ? 'rgba(46,204,113,0.1)' : `${V1}0c`, border: `1px solid ${cur.found ? 'rgba(46,204,113,0.35)' : `${V1}25`}`, color: cur.found ? '#1e9e57' : 'var(--text)', fontWeight: cur.found ? 700 : 500 }}>
        {cur.note}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 22 }}>
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={navBtnStyle(step === 0)}>← Prev</button>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Step {step + 1} of {steps.length}</span>
        <button onClick={() => setStep(s => Math.min(steps.length - 1, s + 1))} disabled={step === steps.length - 1} style={navBtnStyle(step === steps.length - 1)}>Next →</button>
      </div>
    </div>
  );
};
const navBtnStyle = disabled => ({
  padding: '10px 18px', borderRadius: 10, border: `1.5px solid ${disabled ? 'var(--border)' : V1}`,
  background: disabled ? 'transparent' : V1, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 'clamp(13px, 0.4vw + 12px, 14.5px)', fontWeight: 700, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  fontFamily: "'Satoshi','DM Sans',sans-serif",
});

const FallbackQuiz = ({ quiz }) => {
  const [answers, setAnswers] = useState({});
  const pick = (qi, oi) => setAnswers(a => ({ ...a, [qi]: oi }));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22, marginTop: 26 }}>
      {quiz.map((q, qi) => {
        const picked = answers[qi], revealed = picked !== undefined;
        return (
          <div key={qi}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 10 }}>{qi + 1}. {q.q}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {q.options.map((opt, oi) => {
                const isCorrect = oi === q.correct, isPicked = oi === picked;
                let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
                if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
                else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
                return (
                  <button key={oi} onClick={() => !revealed && pick(qi, oi)} disabled={revealed} style={{
                    display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', padding: '10px 14px', borderRadius: 10,
                    border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5, cursor: revealed ? 'default' : 'pointer',
                    fontFamily: "'Satoshi','DM Sans',sans-serif",
                  }}>
                    <span style={{ width: 18, height: 18, borderRadius: '50%', border: `1.5px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: revealed && isCorrect ? GREEN : revealed && isPicked ? RED : 'var(--muted)' }}>
                      {revealed && isCorrect ? <CheckIcon /> : revealed && isPicked ? <XIcon /> : ''}
                    </span>
                    {opt}
                  </button>
                );
              })}
            </div>
            {revealed && <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.6, background: 'var(--bg3)', color: 'var(--text-2)', border: '1px solid var(--border)' }}>{q.explain}</div>}
          </div>
        );
      })}
    </div>
  );
};

const PlaygroundSection = ({ content }) => {
  const Comp = PLAYGROUNDS[content.key];
  if (Comp) return <Comp />;
  if (content.visualizer || content.quiz) {
    return (
      <div>
        {content.visualizer && <FallbackVisualizer visualizer={content.visualizer} />}
        {content.quiz && <FallbackQuiz quiz={content.quiz} />}
      </div>
    );
  }
  return <p style={{ fontSize: 13.5, color: 'var(--muted)' }}>Interactive practice for this pattern is coming soon.</p>;
};

/* ─── Section switch ─────────────────────────────────────────── */
const SectionBody = ({ section, content }) => {
  switch (section.type) {
    case 'text': return <TextSection section={section} />;
    case 'memory-diagram': return <MemorySection section={section} />;
    case 'bucket-diagram': return <BucketSection section={section} />;
    case 'pointer-diagram': return <PointerSection section={section} />;
    case 'window-diagram': return <WindowSection section={section} />;
    case 'stack-diagram': return <StackSection section={section} />;
    case 'node-diagram': return <NodeSection section={section} />;
    case 'tree-diagram': return <TreeSection section={section} />;
    case 'trie-diagram': return <TrieSection section={section} />;
    case 'graph-diagram': return <GraphSection section={section} />;
    case 'dp-table-diagram': return <DpTableSection section={section} />;
    case 'grid-diagram': return <GridDiagramSection section={section} />;
    case 'interval-diagram': return <IntervalDiagramSection section={section} />;
    case 'bit-diagram': return <BitDiagramSection section={section} />;
    case 'code': return <CodeSection section={section} />;
    case 'errors': return <ErrorsSection section={section} />;
    case 'complexity': return <ComplexitySection section={section} />;
    case 'problems': return <ProblemsSection section={section} />;
    case 'cheatsheet': return <CheatsheetSection section={section} />;
    case 'playground': return <PlaygroundSection content={content} />;
    default: return null;
  }
};

/* ─── Modal shell ─────────────────────────────────────────────── */
const DsaExplanationModal = ({ content, onClose }) => {
  const [active, setActive] = useState(0);
  const [visited, setVisited] = useState({ 0: true });
  const bodyRef = useRef(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [active]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  if (!content) return null;
  const sections = content.sections;
  const goTo = i => { setActive(i); setVisited(v => ({ ...v, [i]: true })); };

  return (
    <div className="dsa-exp-overlay" onClick={onClose}>
      <div className="dsa-exp-modal" onClick={e => e.stopPropagation()}>
        <div className="dsa-exp-header">
          <div>
            <div style={{ fontSize: 'clamp(11px, 0.3vw + 10px, 12px)', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: V2, marginBottom: 5 }}>Pattern Explanation</div>
            <h2 style={{ fontFamily: "'Inter',sans-serif", fontSize: 'clamp(24px, 1.6vw + 16px, 29px)', fontWeight: 800, color: 'var(--text)', margin: 0 }}>{content.key}</h2>
            <p style={{ fontSize: 'clamp(13.5px, 0.5vw + 11px, 15px)', color: 'var(--muted)', margin: '5px 0 0' }}>{content.tagline}</p>
          </div>
          <button className="dsa-exp-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="dsa-exp-main">
          <div className="dsa-exp-sidebar">
            {sections.map((s, i) => (
              <button key={s.id} onClick={() => goTo(i)} className={`dsa-exp-navitem ${active === i ? 'active' : ''}`}>
                <span className="dsa-exp-navdot">{visited[i] ? <CheckIcon /> : i + 1}</span>
                <span>{s.title}</span>
              </button>
            ))}
          </div>

          <div className="dsa-exp-body" ref={bodyRef}>
            <h3 style={{ fontFamily: "'Inter',sans-serif", fontSize: 'clamp(20px, 1.4vw + 14px, 24px)', fontWeight: 800, color: 'var(--text)', margin: '0 0 18px' }}>
              {sections[active].title}
            </h3>
            <SectionBody section={sections[active]} content={content} />

            <div className="dsa-exp-footernav">
              <button onClick={() => goTo(Math.max(0, active - 1))} disabled={active === 0} style={navBtnStyle(active === 0)}>← Previous</button>
              <span style={{ fontSize: 'clamp(12.5px, 0.3vw + 11px, 13.5px)', fontWeight: 700, color: 'var(--muted)' }}>{active + 1} / {sections.length}</span>
              <button onClick={() => goTo(Math.min(sections.length - 1, active + 1))} disabled={active === sections.length - 1} style={navBtnStyle(active === sections.length - 1)}>Next →</button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .dsa-exp-overlay {
          position: fixed; inset: 0; z-index: 9200;
          background: rgba(10,6,20,0.72); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; padding: 116px 24px 24px;
          animation: dsaExpFade 0.2s ease;
        }
        .dsa-exp-modal {
          width: min(1280px, 96vw); height: min(87vh, 940px);
          background: var(--card-solid); border: 1px solid var(--border); border-radius: 24px;
          display: flex; flex-direction: column; overflow: hidden;
          box-shadow: 0 30px 90px -12px rgba(0,0,0,0.5);
          animation: dsaExpPop 0.25s cubic-bezier(0.2,1,0.3,1);
        }
        .dsa-exp-header {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
          padding: 28px 34px 22px; border-bottom: 1px solid var(--border); flex-shrink: 0;
        }
        .dsa-exp-close {
          flex-shrink: 0; width: 38px; height: 38px; border-radius: 50%;
          border: 1px solid var(--border); background: var(--bg3); color: var(--muted);
          font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: center;
          transition: background 0.2s;
        }
        .dsa-exp-close:hover { background: var(--bg2); color: var(--text); }

        .dsa-exp-main { flex: 1; display: flex; min-height: 0; }

        .dsa-exp-sidebar {
          width: 275px; flex-shrink: 0; overflow-y: auto; padding: 16px 12px;
          border-right: 1px solid var(--border); display: flex; flex-direction: column; gap: 3px;
          scroll-behavior: smooth; overscroll-behavior: contain; -webkit-overflow-scrolling: touch;
        }
        .dsa-exp-navitem {
          display: flex; align-items: center; gap: 11px; text-align: left;
          padding: 11px 12px; border-radius: 10px; border: none; background: none; cursor: pointer;
          font-size: 13.5px; font-weight: 600; color: var(--muted); line-height: 1.35;
          font-family: 'Satoshi','DM Sans',sans-serif; transition: background 0.15s, color 0.15s;
        }
        .dsa-exp-navitem:hover { background: var(--bg3); color: var(--text); }
        .dsa-exp-navitem.active { background: ${V1}14; color: ${V1}; font-weight: 800; }
        .dsa-exp-navdot {
          flex-shrink: 0; width: 22px; height: 22px; border-radius: 7px; font-size: 11px; font-weight: 800;
          display: flex; align-items: center; justify-content: center;
          background: var(--bg3); color: var(--muted);
        }
        .dsa-exp-navitem.active .dsa-exp-navdot { background: ${V1}; color: #fff; }

        .dsa-exp-body {
          flex: 1; overflow-y: auto; padding: 30px 38px 36px; min-height: 0;
          scroll-behavior: smooth; overscroll-behavior: contain; -webkit-overflow-scrolling: touch;
        }
        .dsa-exp-footernav {
          display: flex; align-items: center; justify-content: space-between;
          margin-top: 34px; padding-top: 20px; border-top: 1px solid var(--border);
        }

        @keyframes dsaExpFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dsaExpPop { from { opacity: 0; transform: scale(0.96) translateY(12px); } to { opacity: 1; transform: none; } }

        @media (prefers-reduced-motion: reduce) {
          .dsa-exp-overlay, .dsa-exp-modal { animation: none !important; }
        }
        @media (max-width: 1180px) {
          .dsa-exp-modal { width: 94vw; }
          .dsa-exp-sidebar { width: 235px; }
        }
        @media (max-width: 760px) {
          .dsa-exp-overlay { padding: 84px 10px 10px; }
          .dsa-exp-modal { width: 98vw; height: 84vh; border-radius: 18px; }
          .dsa-exp-header { padding: 20px 20px 16px; }
          .dsa-exp-main { flex-direction: column; }
          .dsa-exp-sidebar { width: 100%; flex-direction: row; overflow-x: auto; border-right: none; border-bottom: 1px solid var(--border); padding: 12px 10px; }
          .dsa-exp-navitem { flex-shrink: 0; }
          .dsa-exp-navitem span:last-child { white-space: nowrap; }
          .dsa-exp-body { padding: 20px 18px 26px; }
        }
        @media (max-width: 420px) {
          .dsa-exp-overlay { padding: 78px 6px 6px; }
          .dsa-exp-modal { height: 88vh; border-radius: 14px; }
        }
      `}</style>
    </div>
  );
};

export default DsaExplanationModal;
